(function () {
  const strings = window.PieceworkStrings || {};
  const searchStrings = strings.search || {};
  const modalStrings = strings.modal || {};

  // Session-scoped (not localStorage — a stale search shouldn't outlive the
  // browsing session) so navigating to Catalog and back restores the same
  // results without re-generating. Note: R2 signs image URLs for 1 hour, so
  // a restore after sitting idle that long will show broken thumbnails —
  // an acceptable tradeoff for a same-session back/forward flow.
  const SEARCH_STATE_KEY = "pieceworkSearchState";

  // localStorage (not sessionStorage) — unlike the single-slot restore
  // above, "Recent" is meant to survive closing the tab. Only lightweight
  // pointers are stored here (sessionId/phrase/orientation/sizes, NOT
  // preview data or image URLs) — R2 signs image URLs for 1 hour, so
  // anything stored today would show broken thumbnails by tomorrow.
  // Instead, opening a past search re-fetches /api/search-status live,
  // which re-signs fresh URLs every call. This also means Recent can only
  // ever show searches from the last PREVIEW_MAX_AGE_HOURS (72h default,
  // src/cleanupPreviews.js) — after that the backend has actually deleted
  // the previews, and the entry self-heals out of the list on a 404.
  const RECENT_SEARCHES_KEY = "pieceworkRecentSearches";
  const RECENT_SEARCHES_MAX = 20;

  // A random per-browser id, sent with every /api/search call so the
  // backend's per-device rate limit (src/searchRateLimit.js) can tell two
  // different people apart even when they share an IP (same wifi/office).
  // localStorage, not sessionStorage — the point is to identify the same
  // browser across tabs/sessions, not scope to one. Anyone who wants to can
  // spoof this by clearing storage, which is expected: this is a UX-level
  // throttle against accidental self-collisions, not the real security
  // boundary (that's the backend's IP-level limit).
  const DEVICE_ID_KEY = "pieceworkDeviceId";

  function getDeviceId() {
    try {
      let id = localStorage.getItem(DEVICE_ID_KEY);
      if (!id) {
        id = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
        localStorage.setItem(DEVICE_ID_KEY, id);
      }
      return id;
    } catch (err) {
      return null;
    }
  }

  function loadRecentSearches() {
    try {
      const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (err) {
      return [];
    }
  }

  function saveRecentSearches(list) {
    try {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(list.slice(0, RECENT_SEARCHES_MAX)));
    } catch (err) {
      // Storage full/unavailable (e.g. private browsing) — Recent just
      // won't persist, nothing else depends on this succeeding.
    }
  }

  // Recorded once per search action (initial submit only, not per "Load 10
  // more" click — a load-more batch belongs to the same browsing entry
  // point as the search that started it, so it doesn't get its own
  // top-level Recent entry). `sessionId` stays the entry's stable identity
  // (used to find/remove it later); `sessionIds` accumulates every session
  // — the initial one plus one per "Load 10 more" click — since each
  // click starts a genuinely new backend session with its own fresh
  // previews. Without tracking all of them, reopening a past search that
  // had been expanded via "Load 10 more" would only ever show the
  // original 10, not everything that had actually been loaded.
  function recordRecentSearch({ sessionId, phrase, orientation, sizes }) {
    const list = loadRecentSearches();
    list.unshift({ sessionId, sessionIds: [sessionId], phrase, orientation, sizes, createdAt: Date.now() });
    saveRecentSearches(list);
  }

  // Called when a "Load 10 more" click succeeds for a phrase that has a
  // Recent entry — folds the new session into that entry's sessionIds so
  // reopening it later shows everything, not just the original 10.
  function appendSessionIdToRecentEntry(primarySessionId, newSessionId) {
    if (!primarySessionId) return;
    const list = loadRecentSearches();
    const entry = list.find((e) => e.sessionId === primarySessionId);
    if (!entry) return;
    if (!Array.isArray(entry.sessionIds)) entry.sessionIds = [entry.sessionId];
    if (!entry.sessionIds.includes(newSessionId)) entry.sessionIds.push(newSessionId);
    saveRecentSearches(list);
  }

  function removeRecentSearch(sessionId) {
    saveRecentSearches(loadRecentSearches().filter((entry) => entry.sessionId !== sessionId));
  }

  // Case/whitespace-insensitive so "Mansion on Jupiter", "mansion on jupiter"
  // and "  Mansion  on Jupiter " all resolve to the same past search rather
  // than each re-generating a fresh batch.
  function normalizePhrase(phrase) {
    return String(phrase || "").trim().toLowerCase().replace(/\s+/g, " ");
  }

  // Returns the most recent Recent-searches entry whose phrase matches the
  // one just typed (or null). Lets a re-typed search reuse the 10 (or more)
  // designs already generated for that phrase instead of paying to generate
  // 10 brand-new ones — only "Load 10 more" should ever generate more.
  function findRecentByPhrase(phrase) {
    const target = normalizePhrase(phrase);
    if (!target) return null;
    return loadRecentSearches().find((entry) => normalizePhrase(entry.phrase) === target) || null;
  }

  function formatRelativeTime(timestamp) {
    const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }

  // Rotating search-bar placeholder examples — creative content specific to
  // this one field, not translatable UI chrome, so it lives here rather
  // than in the locale file's window.PieceworkStrings. Deliberately busy,
  // many-subject scenes (a cluttered shelf, a crowded market) rather than
  // single-subject ones — those render with more distinct small details,
  // which makes for a more interesting puzzle to actually assemble. Still
  // spans most of the 7 PUZZLE_CATEGORIES (src/generateCopy.js) so the
  // rotation demonstrates the catalog's range, not just one theme.
  const SEARCH_PLACEHOLDER_EXAMPLES = [
    "cluttered wizard's study shelves",
    "toy store shelves overflowing",
    "pirate ship deck in battle",
    "candy shop counter display",
    "messy artist's studio",
    "overgrown greenhouse full of plants",
    "antique shop crammed with curiosities",
    "autumn harvest table spread",
    "underwater shipwreck with sea life",
    "cluttered alchemist's laboratory",
    "overstuffed antique bookstore aisle",
    "messy inventor's workshop",
    "crowded curiosity cabinet shelf",
    "packed spice market stall",
    "witch's kitchen full of ingredients",
    "tide pool full of sea creatures",
    "overgrown cottage garden in bloom",
    "packed fruit stand display",
    "bustling alien bazaar market",
    "crowded zoo panorama",
  ];

  /**
   * Cycles the search input's placeholder through SEARCH_PLACEHOLDER_EXAMPLES
   * on an interval. Only rewrites it while the field is idle and empty —
   * skips a tick rather than yanking the placeholder out from under someone
   * mid-focus (a placeholder is invisible once there's real input anyway,
   * so this is really about not fighting the browser's own focus styling).
   */
  function startPlaceholderRotation(input) {
    if (!input || SEARCH_PLACEHOLDER_EXAMPLES.length === 0) return;
    let index = 0;
    const rotate = () => {
      if (document.activeElement === input || input.value) return;
      input.placeholder = `Try: ${SEARCH_PLACEHOLDER_EXAMPLES[index]}`;
      index = (index + 1) % SEARCH_PLACEHOLDER_EXAMPLES.length;
    };
    rotate();
    setInterval(rotate, 2600);
  }

  // Matches src/generatePreviews.js's STYLE_MODIFIERS.length — a search
  // always targets this many previews, whether or not all of them land.
  const EXPECTED_PREVIEW_COUNT = 10;

  /**
   * Polls POST /api/search-status until the session reports done or the
   * attempt budget runs out. Each tick fetches the FULL list-so-far (the
   * backend doesn't track deltas), so this diffs against `knownIds`
   * internally — callers only ever see genuinely new previews via onUpdate,
   * never a repeat.
   *
   * 1500ms interval, 40 attempts (~60s budget). That budget starts only
   * once polling begins, i.e. after the initial POST /api/search response
   * already landed — so a Render cold-start delay (up to ~50s) on that
   * first call doesn't eat into it.
   */
  function pollSearchStatus({ sessionId, backendUrl, knownIds, onUpdate, onDone, onError }) {
    const INTERVAL_MS = 1500;
    const MAX_ATTEMPTS = 40;
    let attempts = 0;

    async function tick() {
      attempts++;
      let response, data;
      try {
        response = await fetch(`${backendUrl}/api/search-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId }),
        });
        data = await response.json();
      } catch (err) {
        onError(err.message);
        return;
      }

      if (!response.ok) {
        onError(data.error);
        return;
      }

      const newPreviews = data.previews.filter((p) => !knownIds.has(p.id));
      newPreviews.forEach((p) => knownIds.add(p.id));
      if (newPreviews.length > 0) onUpdate(newPreviews, data);

      if (data.done || attempts >= MAX_ATTEMPTS) {
        onDone(data);
        return;
      }
      setTimeout(tick, INTERVAL_MS);
    }

    tick();
  }

  /**
   * Fetches every session in `sessionIds` live via /api/search-status in
   * parallel and combines their previews. Used by both restoreState() (a
   * sessionStorage restore, e.g. after the computer slept and the page got
   * refreshed) and loadPastSearch() (picking a Recent entry) — both need
   * the exact same thing: don't trust locally-cached image URLs, always
   * ask the backend for freshly-signed ones, since R2 signs them for only
   * 1 hour and a stale local copy would just show broken images.
   *
   * Resolves to null if every session has aged out (most likely past
   * src/cleanupPreviews.js's 72h retention window) — the caller decides
   * how to handle that (drop the whole entry vs. clear sessionStorage).
   * If only some sessions expired, resolves with just the survivors and a
   * `sessionIds` list trimmed to match, so the caller can prune the dead
   * ones from wherever it's persisting this list.
   */
  function fetchSessionsLive({ backendUrl, sessionIds }) {
    return Promise.all(
      sessionIds.map((sessionId) =>
        fetch(`${backendUrl}/api/search-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId }),
        })
          .then(async (response) => ({ ok: response.ok, sessionId, data: await response.json() }))
          .catch(() => ({ ok: false, sessionId, data: null }))
      )
    ).then((results) => {
      const succeeded = results.filter((r) => r.ok);
      if (succeeded.length === 0) return null;
      return {
        previews: succeeded.flatMap((r) => r.data.previews),
        done: succeeded.every((r) => r.data.done),
        orientation: succeeded[0].data.orientation,
        sessionIds: succeeded.map((r) => r.sessionId),
        partial: succeeded.length < results.length,
      };
    });
  }

  function titleCase(text) {
    return text
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word[0].toUpperCase() + word.slice(1))
      .join(" ");
  }

  function formatMoney(amount) {
    return `$${Number(amount).toFixed(2)}`;
  }

  // textContent/innerHTML round-tripping only encodes &/</> — leaves quotes
  // alone, which is unsafe wherever the result lands inside a double-quoted
  // HTML attribute (e.g. alt="${escapeHtml(...)}"), not just a text node.
  function escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, (ch) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]
    ));
  }

  // Toggles the small spinner inside the search input itself — distinct
  // from setStatus()'s message below the hero, this is the immediate,
  // in-input feedback that a search is in flight. Paired 1:1 with every
  // submitButton.disabled toggle below, since "disabled" is already this
  // file's correct in-flight signal.
  function setInputLoading(container, isLoading) {
    const wrap = container.querySelector(".ai-search__input-wrap");
    if (wrap) wrap.classList.toggle("is-loading", isLoading);
  }

  function setStatus(container, text, kind, opts) {
    if (!(opts && opts.keepRotation)) stopLoadingRotation();
    const statusEl = container.querySelector("[data-ai-search-status]");
    if (!text) {
      statusEl.hidden = true;
      statusEl.textContent = "";
      statusEl.className = "ai-search__status";
      return;
    }
    // .ai-search has no padding by default (it's dead space on a fresh
    // homepage load, before any search) — this class turns it back on the
    // moment there's actually something to show.
    container.querySelector(".ai-search").classList.add("has-content");
    statusEl.hidden = false;
    statusEl.textContent = text;
    statusEl.className = `ai-search__status ${kind || ""}`;
  }

  // The initial "loading" status (shown before the first real preview or
  // progress count lands) cycles through a few phrases every 3.5s rather than
  // sitting on one static line the whole time — the last phrase just stays
  // put until something else (showProgress, an error, etc.) calls setStatus
  // and naturally overwrites it, which is also what stops the rotation
  // (setStatus() clears the timer on every call except its own ticks).
  let loadingRotationTimer = null;

  function stopLoadingRotation() {
    if (loadingRotationTimer) {
      clearInterval(loadingRotationTimer);
      loadingRotationTimer = null;
    }
  }

  function startLoadingRotation(container) {
    const phrases = [
      searchStrings.loading || "Searching through millions of puzzles for the perfect one...",
      searchStrings.loading2 || "This can take up to 13 seconds...",
      searchStrings.loading3 || "Sketching a few different art styles...",
      searchStrings.loading4 || "Almost there, putting the finishing touches on your puzzles...",
    ];
    stopLoadingRotation();
    let index = 0;
    setStatus(container, phrases[index], "loading", { keepRotation: true });
    loadingRotationTimer = setInterval(() => {
      index += 1;
      setStatus(container, phrases[index], "loading", { keepRotation: true });
      if (index >= phrases.length - 1) {
        clearInterval(loadingRotationTimer);
        loadingRotationTimer = null;
      }
    }, 3500);
  }

  // One modal, shared by every card click — built once and reused rather
  // than rebuilt per open, so state (selected size, in-flight request) is
  // easy to reset consistently.
  function buildModal() {
    const overlay = document.createElement("div");
    overlay.className = "preview-modal-overlay";
    overlay.innerHTML = `
      <div class="preview-modal" role="dialog" aria-modal="true">
        <button type="button" class="preview-modal__close" data-modal-close aria-label="${modalStrings.close || "Close"}">&times;</button>
        <img class="preview-modal__image" data-modal-image alt="">
        <div class="preview-modal__body">
          <h3 data-modal-title></h3>
          <p class="preview-modal__description" data-modal-description hidden></p>
          <div class="preview-modal__sizes" data-modal-sizes></div>
          <div class="preview-modal__dimensions" data-modal-dimensions></div>
          <div class="preview-modal__status" data-modal-status hidden></div>
          <div class="preview-modal__actions">
            <button type="button" class="btn btn-secondary preview-modal__add-to-cart" data-modal-add-to-cart>${modalStrings.addToCart || "Add to cart"}</button>
            <button type="button" class="btn btn-primary preview-modal__buy" data-modal-buy>${modalStrings.buy || "Buy"}</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") overlay.classList.remove("is-open");
    });

    return overlay;
  }

  function openPreviewModal({ preview, sizes, backendUrl, orientation }) {
    let modal = document.querySelector(".preview-modal-overlay");
    if (!modal) modal = buildModal();

    const label = preview.label || titleCase(preview.styleModifier || "");
    const heading = preview.title || label;
    const imageEl = modal.querySelector("[data-modal-image]");
    // Show the grid thumbnail immediately (already downloaded/cached from
    // the card) so switching between previews feels instant, then preload
    // the full-res master in the background and swap up once it's ready —
    // otherwise the old preview's image lingers on screen until the new
    // full-res image finishes downloading. Same preload-then-swap pattern
    // as product-form.js's setMainImage().
    const fullSrc = preview.fullImageUrl || preview.thumbnailUrl;
    imageEl.dataset.requestedSrc = fullSrc;
    if (preview.thumbnailUrl) imageEl.src = preview.thumbnailUrl;
    if (fullSrc && fullSrc !== preview.thumbnailUrl) {
      const preloaded = new Image();
      preloaded.onload = () => {
        // Only apply if a later click hasn't since requested a different
        // image — avoids a slow preload clobbering a newer preview's image.
        if (imageEl.dataset.requestedSrc === fullSrc) imageEl.src = fullSrc;
      };
      preloaded.src = fullSrc;
    } else {
      imageEl.src = fullSrc;
    }
    imageEl.alt = heading;
    imageEl.classList.toggle("is-orientation-vertical", orientation === "vertical");
    imageEl.classList.toggle("is-orientation-horizontal", orientation !== "vertical");
    modal.querySelector("[data-modal-title]").textContent = heading;

    // Overrides the orientation class's static master-size ratio with the
    // selected size's real print ratio, so the (already center-cropped
    // server-side, see cropToRatio.js) framing a shopper sees here matches
    // what actually prints — object-fit: cover means this is the same
    // symmetric center-crop, just done in CSS instead of pixels.
    function setImageCropRatio(size) {
      if (size && size.width && size.height) {
        imageEl.style.aspectRatio = `${size.width} / ${size.height}`;
      } else {
        imageEl.style.aspectRatio = "";
      }
    }

    const descriptionEl = modal.querySelector("[data-modal-description]");
    if (preview.description) {
      descriptionEl.hidden = false;
      descriptionEl.textContent = preview.description;
    } else {
      descriptionEl.hidden = true;
      descriptionEl.textContent = "";
    }

    const statusEl = modal.querySelector("[data-modal-status]");
    statusEl.hidden = true;
    statusEl.textContent = "";

    const buyLabel = modalStrings.buy || "Buy";
    const buyButton = modal.querySelector("[data-modal-buy]");
    buyButton.disabled = false;
    buyButton.textContent = buyLabel;

    let selectedPieces = sizes[0]?.pieces;
    const sizesEl = modal.querySelector("[data-modal-sizes]");
    const dimensionsEl = modal.querySelector("[data-modal-dimensions]");
    sizesEl.innerHTML = "";
    sizes.forEach((size, i) => {
      const pill = document.createElement("button");
      pill.type = "button";
      pill.className = "size-pill" + (i === 0 ? " is-selected" : "");
      // Price isn't repeated here — it's already shown once, in the "Buy"
      // button's label below (which updates on selection), so pill + button
      // both showing it read as cluttered. Same fix as the Hot Right Now
      // quick-view modal's size pills.
      pill.textContent = `${size.pieces} pc`;
      pill.addEventListener("click", () => {
        selectedPieces = size.pieces;
        sizesEl.querySelectorAll(".size-pill").forEach((p) => p.classList.remove("is-selected"));
        pill.classList.add("is-selected");
        buyButton.textContent = `${buyLabel} — ${formatMoney(size.price)}`;
        dimensionsEl.textContent = size.width && size.height ? `${size.width}" x ${size.height}"` : "";
        setImageCropRatio(size);
      });
      sizesEl.appendChild(pill);
    });
    buyButton.textContent = sizes[0] ? `${buyLabel} — ${formatMoney(sizes[0].price)}` : buyLabel;
    dimensionsEl.textContent = sizes[0]?.width && sizes[0]?.height ? `${sizes[0].width}" x ${sizes[0].height}"` : "";
    setImageCropRatio(sizes[0]);

    const addToCartButton = modal.querySelector("[data-modal-add-to-cart]");
    addToCartButton.onclick = () => {
      const size = sizes.find((s) => s.pieces === selectedPieces) || sizes[0];
      if (!size || !window.PieceworkCart) return;
      window.PieceworkCart.addPreview({
        previewId: preview.id,
        pieces: size.pieces,
        price: size.price,
        title: `${heading} — ${size.pieces} pc`,
        thumbnailUrl: preview.thumbnailUrl,
        styleModifier: preview.styleModifier,
        searchPhrase: preview.searchPhrase,
        // Cart-side size swap (piecework-cart.js) needs the full size list
        // to build its pill panel, plus the heading without the size
        // suffix so it can rebuild `title` cleanly on a swap instead of
        // regex-stripping the old one back off.
        sizes,
        baseTitle: heading,
        // Full-res image, description and orientation aren't needed for
        // anything else in the cart drawer's own rendering, but the cart's
        // "open this puzzle" preview (piecework-cart.js) needs them to show
        // the same larger image/description/print-shape this modal shows,
        // without re-fetching the preview from the backend.
        fullImageUrl: fullSrc,
        description: preview.description || null,
        orientation,
      });
      window.PieceworkCart.flashAdded(addToCartButton, modalStrings.added);
    };

    buyButton.onclick = async () => {
      buyButton.disabled = true;
      statusEl.hidden = false;
      statusEl.textContent = modalStrings.startingCheckout || "Starting checkout...";
      try {
        const response = await fetch(`${backendUrl}/api/select`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ previewId: preview.id, pieces: selectedPieces }),
        });
        const data = await response.json();
        if (!response.ok || !data.checkoutUrl) {
          statusEl.textContent = data.error || modalStrings.checkoutError || "Couldn't start checkout. Please try again.";
          buyButton.disabled = false;
          return;
        }
        window.location.href = data.checkoutUrl;
      } catch (err) {
        statusEl.textContent = modalStrings.networkError || "Network error — please try again.";
        buyButton.disabled = false;
      }
    };

    function closeModal() {
      modal.classList.remove("is-open");
    }

    modal.querySelectorAll("[data-modal-close]").forEach((btn) => {
      btn.onclick = closeModal;
    });
    modal.onclick = (event) => {
      if (event.target === modal) closeModal();
    };

    // Force a reflow before toggling .is-open — needed for a just-built
    // modal specifically (buildModal() inserting the element and this
    // adding .is-open in the same tick means the browser never paints the
    // "closed" state as a real frame first, so the scale/fade transition
    // doesn't play on the very first open). Same fix as the cart drawer.
    void modal.offsetWidth;
    modal.classList.add("is-open");
  }

  // Built once, reused — same rationale as buildModal() above. Deliberately
  // text-only (phrase + relative time), not thumbnails: showing thumbnails
  // per row would mean fetching every listed session's data just to open
  // the list, instead of one fetch only when a specific entry is actually
  // picked.
  function buildRecentModal() {
    const overlay = document.createElement("div");
    overlay.className = "recent-modal-overlay";
    overlay.innerHTML = `
      <div class="recent-modal" role="dialog" aria-modal="true">
        <button type="button" class="recent-modal__close" data-recent-modal-close aria-label="${modalStrings.close || "Close"}">&times;</button>
        <h3>${searchStrings.recentHeading || "Your recent searches"}</h3>
        <ul class="recent-modal__list" data-recent-modal-list></ul>
      </div>
    `;
    document.body.appendChild(overlay);

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") overlay.classList.remove("is-open");
    });

    overlay.querySelectorAll("[data-recent-modal-close]").forEach((btn) => {
      btn.onclick = () => overlay.classList.remove("is-open");
    });
    overlay.onclick = (event) => {
      if (event.target === overlay) overlay.classList.remove("is-open");
    };

    return overlay;
  }

  // onSelect(entry) is called with the clicked entry and is responsible for
  // actually loading it — this function only owns the list UI, same
  // separation as pollSearchStatus's callback shape elsewhere in this file.
  function openRecentModal(onSelect) {
    let modal = document.querySelector(".recent-modal-overlay");
    if (!modal) modal = buildRecentModal();

    const list = modal.querySelector("[data-recent-modal-list]");
    list.innerHTML = "";

    const recent = loadRecentSearches();
    if (recent.length === 0) {
      // The toggle is shown to everyone, including first-time visitors with
      // no history, so the modal needs a graceful empty state rather than a
      // bare heading over an empty list.
      const empty = document.createElement("li");
      empty.className = "recent-modal__empty";
      empty.textContent = searchStrings.recentEmpty || "No recent searches yet.";
      list.appendChild(empty);
    }

    recent.forEach((entry) => {
      const item = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "recent-modal__item";
      button.innerHTML = `
        <span class="recent-modal__item-phrase">${escapeHtml(entry.phrase)}</span>
        <span class="recent-modal__item-time">${escapeHtml(formatRelativeTime(entry.createdAt))}</span>
      `;
      button.addEventListener("click", () => {
        modal.classList.remove("is-open");
        onSelect(entry);
      });
      item.appendChild(button);
      list.appendChild(item);
    });

    // Same first-open reflow fix as openPreviewModal()/the cart drawer.
    void modal.offsetWidth;
    modal.classList.add("is-open");
  }

  // Every preview reaching here is a successful generation by construction
  // (see src/server.js's serializePreviewRow) — a style that ultimately
  // fails just never produces a row, so there's no per-card failure state
  // to render anymore. "Some styles didn't make it" now shows up only as
  // the final count landing under EXPECTED_PREVIEW_COUNT once polling
  // reports done, not as an explicit failed placeholder card.
  function buildResultCard(preview, sizes, backendUrl, orientation, orientationClass) {
    const card = document.createElement("button");
    card.type = "button";
    card.className = "ai-search__card";
    const label = preview.label || titleCase(preview.styleModifier || "");
    // The AI-generated title is the primary heading on the card (real
    // product-listing feel); the raw style modifier becomes a small
    // secondary tag underneath, same role .product-card__price plays.
    const heading = preview.title || label;
    card.innerHTML = `
      <span class="ai-search__card-notch" aria-hidden="true"></span>
      <div class="ai-search__card-image ${orientationClass}">
        <img src="${escapeHtml(preview.thumbnailUrl)}" alt="${escapeHtml(heading)}" loading="lazy">
      </div>
      <div class="ai-search__card-title">${escapeHtml(heading)}</div>
      <div class="ai-search__card-style">${escapeHtml(label)}</div>
    `;
    card.addEventListener("click", () => openPreviewModal({ preview, sizes, backendUrl, orientation }));
    return card;
  }

  // Fades a just-inserted card in from a slightly-scaled-down state,
  // staggered by delayMs so a batch of cards arriving together reveals
  // as a ripple rather than all at once. `.is-entering`/`.is-visible` are
  // removed again once settled — the entrance is a one-shot effect, not
  // a permanent style override, so the card falls back to the base
  // `.ai-search__card` rule (and its own hover-lift transition) for
  // everything after.
  function revealCard(card, delayMs) {
    card.classList.add("is-entering");
    // Force a reflow so the browser paints the "entering" (opacity:0)
    // state as a real frame before the timeout below flips it to
    // visible — without this, a card inserted and revealed in the same
    // tick can skip straight to its end state instead of transitioning.
    void card.offsetWidth;
    setTimeout(() => {
      card.classList.add("is-visible");
      setTimeout(() => {
        card.classList.remove("is-entering", "is-visible");
      }, 320);
    }, delayMs);
  }

  const ASSEMBLY_SHARD_COUNT = 3;
  const ASSEMBLY_SHARD_STAGGER_MS = 70;
  const ASSEMBLY_SHARD_DURATION_MS = 500;

  // Phase 3's "assembly reveal" — the card enters as 3 jagged clipped
  // shards (theme.css's .ai-search__card-shard--1/2/3, sharing exact
  // clip-path boundaries so they tile with no seam) that fly in from
  // offset/rotated positions and settle with a bounce. Falls back to the
  // plain Phase 2 fade (revealCard) under prefers-reduced-motion or
  // missing clip-path support, since this is a bigger, different effect
  // rather than something that degrades gracefully in place.
  function revealCardAssembly(card, delayMs) {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const supportsClipPath =
      window.CSS && CSS.supports && CSS.supports("clip-path", "polygon(0% 0%, 100% 0%, 100% 100%)");
    if (prefersReducedMotion || !supportsClipPath) {
      revealCard(card, delayMs);
      return;
    }

    const innerHTML = card.innerHTML;
    card.classList.add("is-assembling");

    const shards = [];
    for (let i = 1; i <= ASSEMBLY_SHARD_COUNT; i++) {
      const shard = document.createElement("div");
      shard.className = `ai-search__card-shard ai-search__card-shard--${i}`;
      shard.setAttribute("aria-hidden", "true");
      shard.innerHTML = innerHTML;
      card.appendChild(shard);
      shards.push(shard);
    }

    // Force a reflow before assembling so the browser paints the offset
    // "flying in" state as a real frame first — same forced-reflow fix
    // used everywhere else a lazily-inserted element gets an immediate
    // class flip (cart drawer, preview modal, etc.).
    void card.offsetWidth;

    setTimeout(() => {
      shards.forEach((shard, i) => {
        setTimeout(() => shard.classList.add("is-assembled"), i * ASSEMBLY_SHARD_STAGGER_MS);
      });
      const lastShardDelay = (ASSEMBLY_SHARD_COUNT - 1) * ASSEMBLY_SHARD_STAGGER_MS;
      setTimeout(() => {
        shards.forEach((shard) => shard.remove());
        card.classList.remove("is-assembling");
      }, lastShardDelay + ASSEMBLY_SHARD_DURATION_MS + 80);
    }, delayMs);
  }

  function appendResults(container, previews, sizes, backendUrl, orientation) {
    const grid = container.querySelector("[data-ai-search-results]");
    const orientationClass = orientation === "vertical" ? "is-orientation-vertical" : "is-orientation-horizontal";
    previews.forEach((preview, index) => {
      const card = buildResultCard(preview, sizes, backendUrl, orientation, orientationClass);
      grid.appendChild(card);
      revealCardAssembly(card, index * 50);
    });
  }

  // Skeleton placeholders shown the moment a search/load-more kicks off
  // (before any real preview has arrived) — shimmer cards standing in
  // for the eventual real ones, replaced one at a time as results
  // actually land via replaceSkeletonsWithResults() below. Purely
  // cosmetic (no data), so a plain <div> rather than the real cards'
  // <button>.
  function createSkeletonCard() {
    const card = document.createElement("div");
    card.className = "ai-search__card ai-search__card--skeleton";
    card.innerHTML = `
      <span class="ai-search__card-notch" aria-hidden="true"></span>
      <div class="ai-search__card-image"></div>
      <div class="ai-search__card-title-skeleton"></div>
      <div class="ai-search__card-style-skeleton"></div>
    `;
    return card;
  }

  function showSkeletons(container, count) {
    const grid = container.querySelector("[data-ai-search-results]");
    for (let i = 0; i < count; i++) {
      grid.appendChild(createSkeletonCard());
    }
  }

  // Swaps the first `previews.length` skeleton placeholders still in the
  // grid for real cards, in place — keeps results landing in a stable
  // grid position instead of skeletons and real cards both piling up
  // side by side. Falls back to appending if there aren't enough
  // skeletons left (shouldn't normally happen, but not worth crashing
  // over if it does).
  function replaceSkeletonsWithResults(container, previews, sizes, backendUrl, orientation) {
    const grid = container.querySelector("[data-ai-search-results]");
    const orientationClass = orientation === "vertical" ? "is-orientation-vertical" : "is-orientation-horizontal";
    previews.forEach((preview, index) => {
      const skeleton = grid.querySelector(".ai-search__card--skeleton");
      const card = buildResultCard(preview, sizes, backendUrl, orientation, orientationClass);
      if (skeleton) {
        grid.replaceChild(card, skeleton);
      } else {
        grid.appendChild(card);
      }
      revealCardAssembly(card, index * 50);
    });
  }

  // Called once a batch reports done (or errors) — any skeleton left
  // over (a style that never made it, or a request that errored out
  // mid-poll) shouldn't shimmer forever.
  function removeRemainingSkeletons(container) {
    container.querySelectorAll(".ai-search__card--skeleton").forEach((el) => el.remove());
  }

  // A fresh "Load 10 more" button is created after every batch (including
  // the first search) and replaces the previous one, so there's always
  // exactly one at the bottom, directly under the batch it belongs to.
  // onBatchLoaded lets the caller fold the new previews into its own
  // session-persisted state without this function knowing about storage.
  // onNewSession (optional) is called with each fresh sessionId a "Load 10
  // more" click generates, as soon as it's known — lets the caller fold it
  // into the originating Recent-searches entry (see
  // appendSessionIdToRecentEntry) so reopening that entry later shows
  // everything that was ever loaded, not just the first 10.
  function appendLoadMoreButton(container, { phrase, backendUrl, onBatchLoaded, onNewSession }) {
    const slot = container.querySelector("[data-ai-search-load-more-slot]");
    slot.innerHTML = "";

    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn btn-secondary ai-search__load-more";
    button.textContent = searchStrings.loadMore || "Load 10 more";

    function resetButton() {
      button.disabled = false;
      button.textContent = searchStrings.loadMore || "Load 10 more";
    }

    button.addEventListener("click", async () => {
      button.disabled = true;
      button.textContent = searchStrings.loadingMore || "Generating 10 more...";
      try {
        const response = await fetch(`${backendUrl}/api/search`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phrase, deviceId: getDeviceId() }),
        });
        const data = await response.json();
        if (!response.ok || data.blocked) {
          setStatus(container, data.message || searchStrings.genericError || "Something went wrong. Please try again.", data.blocked ? "blocked" : "error");
          resetButton();
          return;
        }
        if (onNewSession) onNewSession(data.sessionId);

        const knownIds = new Set();
        showSkeletons(container, EXPECTED_PREVIEW_COUNT);
        pollSearchStatus({
          sessionId: data.sessionId,
          backendUrl,
          knownIds,
          onUpdate: (newPreviews) => {
            replaceSkeletonsWithResults(container, newPreviews, data.sizes, backendUrl, data.orientation);
            onBatchLoaded(newPreviews);
          },
          onDone: () => {
            removeRemainingSkeletons(container);
            appendLoadMoreButton(container, { phrase, backendUrl, onBatchLoaded, onNewSession });
          },
          onError: () => {
            removeRemainingSkeletons(container);
            resetButton();
          },
        });
      } catch (err) {
        resetButton();
      }
    });

    slot.appendChild(button);
  }

  function initAiSearch(container) {
    const form = container.querySelector("[data-ai-search-form]");
    const input = container.querySelector("[data-ai-search-input]");
    const submitButton = container.querySelector("[data-ai-search-submit]");
    const backendUrl = container.dataset.backendUrl;

    startPlaceholderRotation(input);

    // The single source of truth for "what's on screen right now" — saved
    // to sessionStorage after every change so a later page load (e.g. from
    // Catalog -> back) can rebuild the exact same grid.
    let state = null;

    function persistState() {
      if (!state) return;
      try {
        sessionStorage.setItem(SEARCH_STATE_KEY, JSON.stringify(state));
      } catch (err) {
        // Storage full/unavailable (e.g. private browsing) — results just
        // won't survive navigation, nothing else depends on this succeeding.
      }
    }

    // isDone=false uses "so far" phrasing mid-poll; isDone=true switches to
    // the "click one to choose a size" phrasing once nothing more is coming
    // (or the polling budget ran out — same terminal treatment either way).
    // Denominator is Math.max(EXPECTED_PREVIEW_COUNT, foundCount), not the
    // bare constant: mid-poll (foundCount <= 10) it's the known per-batch
    // target ("3/10"), but state.previews accumulates across "Load 10 more"
    // batches, so after even one load-more foundCount legitimately exceeds
    // 10 — without this it showed a nonsensical "20/10" on sessionStorage
    // restore. Once foundCount > 10 the denominator just tracks it 1:1
    // ("20/20"), which reads correctly for the common case.
    function showProgress(foundCount, isDone) {
      const total = Math.max(EXPECTED_PREVIEW_COUNT, foundCount);
      const summary = isDone
        ? searchStrings.resultsSummary || "puzzles found. Click one to choose a size."
        : searchStrings.resultsProgress || "puzzles found so far...";
      setStatus(container, `${foundCount}/${total} ${summary}`, "loading");
    }

    // primarySessionId (optional) is the Recent-searches entry this batch
    // belongs to, if any — passed through so a "Load 10 more" click here
    // folds its new session into that entry (see appendSessionIdToRecentEntry)
    // instead of silently not being there next time Recent is reopened.
    // Every new session also gets folded into the live `state.sessionIds`
    // (and re-persisted) so a same-session sessionStorage restore later
    // re-fetches everything that was ever loaded, not just the first 10.
    function startLoadMore(phrase, primarySessionId) {
      appendLoadMoreButton(container, {
        phrase,
        backendUrl,
        onBatchLoaded: (newPreviews) => {
          state.previews = state.previews.concat(newPreviews);
          persistState();
        },
        onNewSession: (newSessionId) => {
          if (state) {
            if (!Array.isArray(state.sessionIds)) state.sessionIds = state.sessionId ? [state.sessionId] : [];
            if (!state.sessionIds.includes(newSessionId)) state.sessionIds.push(newSessionId);
            persistState();
          }
          if (primarySessionId) appendSessionIdToRecentEntry(primarySessionId, newSessionId);
        },
      });
    }

    // Per an explicit scope decision (see docs/decisions-log.md's "Search
    // latency, round 2"): restoring a session that was still mid-poll when
    // the user navigated away does NOT resume polling — it just shows
    // whatever had already landed and treats that as final. Simpler first
    // cut; revisit only if this turns out to matter in practice.
    //
    // Re-fetches every session live via fetchSessionsLive() rather than
    // rendering `saved.previews` directly — those previews carry image
    // URLs that were signed at search time, and R2 only signs them for 1
    // hour. Rendering them straight from sessionStorage meant a restore
    // after the computer slept for an hour (or any similarly long gap)
    // showed broken image icons instead of the actual puzzles, even though
    // the previews themselves were still perfectly fine server-side.
    function restoreState() {
      let saved;
      try {
        const raw = sessionStorage.getItem(SEARCH_STATE_KEY);
        saved = raw ? JSON.parse(raw) : null;
      } catch (err) {
        return;
      }
      if (!saved || !saved.phrase || !Array.isArray(saved.previews) || saved.previews.length === 0) return;

      const sessionIds =
        Array.isArray(saved.sessionIds) && saved.sessionIds.length > 0
          ? saved.sessionIds
          : saved.sessionId
          ? [saved.sessionId]
          : [];
      if (sessionIds.length === 0) {
        // No session info at all — very old persisted state from before
        // this was tracked. Fall back to the old behavior (render whatever
        // was saved, stale URLs and all) rather than showing nothing.
        state = saved;
        input.value = saved.phrase;
        showProgress(saved.previews.length, true);
        appendResults(container, saved.previews, saved.sizes, backendUrl, saved.orientation);
        startLoadMore(saved.phrase, saved.sessionId);
        return;
      }

      input.value = saved.phrase;
      startLoadingRotation(container);

      fetchSessionsLive({ backendUrl, sessionIds }).then((result) => {
        if (!result) {
          // Every session this state had is gone (aged out of
          // src/cleanupPreviews.js's 72h retention window) — nothing left
          // to restore.
          setStatus(container, searchStrings.recentExpired || "That search is no longer available.", "error");
          try {
            sessionStorage.removeItem(SEARCH_STATE_KEY);
          } catch (err) {}
          return;
        }
        state = {
          phrase: saved.phrase,
          orientation: result.orientation,
          sizes: saved.sizes,
          previews: result.previews,
          sessionId: saved.sessionId,
          sessionIds: result.sessionIds,
        };
        appendResults(container, result.previews, saved.sizes, backendUrl, result.orientation);
        showProgress(result.previews.length, result.done);
        persistState();
        startLoadMore(saved.phrase, saved.sessionId);
      });
    }

    // Always visible (no longer gated on history existing) — the Recent
    // modal shows an empty state of its own when there's nothing yet, so
    // first-time visitors get a sensible result rather than a dead link.
    const recentToggle = container.querySelector("[data-ai-recent-toggle]");

    // Loads a past search picked from the Recent modal. Re-fetches every
    // session this entry has accumulated (the original search plus one
    // per "Load 10 more" click) live via fetchSessionsLive() — not the
    // stored entry's data, there isn't any beyond the pointers — so image
    // URLs are always freshly signed, and the full set that was actually
    // loaded comes back, not just the original 10.
    // opts.fallbackToFresh: when this entry has fully expired server-side,
    // generate a brand-new search for its phrase instead of showing the
    // "no longer available" error. Set when the entry was reached by
    // re-typing the phrase in the search box (the shopper wants results for
    // it either way); left off when reached from the Recent modal, where the
    // explicit "expired" message is the right answer.
    function loadPastSearch(entry, opts) {
      const fallbackToFresh = !!(opts && opts.fallbackToFresh);
      submitButton.disabled = true;
      setInputLoading(container, true);
      container.querySelector("[data-ai-search-results]").innerHTML = "";
      container.querySelector("[data-ai-search-load-more-slot]").innerHTML = "";
      state = null;
      input.value = entry.phrase;
      startLoadingRotation(container);

      const sessionIds =
        Array.isArray(entry.sessionIds) && entry.sessionIds.length > 0 ? entry.sessionIds : [entry.sessionId];

      fetchSessionsLive({ backendUrl, sessionIds }).then((result) => {
        if (!result) {
          // Every session this entry ever accumulated is gone (most likely
          // aged out of src/cleanupPreviews.js's 72h sweep) — self-heal by
          // dropping the whole entry instead of leaving a permanently
          // broken one.
          removeRecentSearch(entry.sessionId);
          if (fallbackToFresh) {
            runFreshSearch(entry.phrase);
            return;
          }
          setStatus(container, searchStrings.recentExpired || "That search is no longer available.", "error");
          submitButton.disabled = false;
          setInputLoading(container, false);
          return;
        }

        // Some (but not all) sessions expired — prune just the dead ones
        // from this entry instead of dropping the still-good previews too.
        if (result.partial) {
          const list = loadRecentSearches();
          const savedEntry = list.find((e) => e.sessionId === entry.sessionId);
          if (savedEntry) {
            savedEntry.sessionIds = result.sessionIds;
            saveRecentSearches(list);
          }
        }

        state = {
          phrase: entry.phrase,
          orientation: result.orientation,
          sizes: entry.sizes,
          previews: result.previews,
          sessionId: entry.sessionId,
          sessionIds: result.sessionIds,
        };
        appendResults(container, result.previews, entry.sizes, backendUrl, result.orientation);
        showProgress(result.previews.length, result.done);
        persistState();
        startLoadMore(entry.phrase, entry.sessionId);
        submitButton.disabled = false;
        setInputLoading(container, false);
      });
    }

    if (recentToggle) {
      recentToggle.addEventListener("click", () => openRecentModal(loadPastSearch));
    }

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const phrase = input.value.trim();
      if (!phrase || !backendUrl) return;

      // Re-typing a phrase that was already searched reuses its existing
      // designs (and folds any "Load 10 more" batches back in) instead of
      // paying to generate 10 brand-new ones. fallbackToFresh so a phrase
      // whose cached previews have since expired still returns results.
      const existing = findRecentByPhrase(phrase);
      if (existing) {
        loadPastSearch(existing, { fallbackToFresh: true });
        return;
      }

      runFreshSearch(phrase);
    });

    // Generates a genuinely new search session (10 fresh previews) for a
    // phrase with no reusable Recent entry — the path taken on a first-time
    // search, and the expiry fallback for a re-typed one.
    async function runFreshSearch(phrase) {
      submitButton.disabled = true;
      setInputLoading(container, true);
      container.querySelector("[data-ai-search-results]").innerHTML = "";
      container.querySelector("[data-ai-search-load-more-slot]").innerHTML = "";
      state = null;
      try {
        sessionStorage.removeItem(SEARCH_STATE_KEY);
      } catch (err) {}
      startLoadingRotation(container);

      // Submit button stays disabled through the whole poll, not just the
      // initial request — re-enabled from whichever terminal branch below
      // actually runs (blocked, network error, status error, or done).
      try {
        const response = await fetch(`${backendUrl}/api/search`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phrase, deviceId: getDeviceId() }),
        });
        const data = await response.json();

        if (!response.ok) {
          setStatus(container, data.error || searchStrings.genericError || "Something went wrong. Please try again.", "error");
          submitButton.disabled = false;
          setInputLoading(container, false);
          return;
        }
        if (data.blocked) {
          setStatus(container, data.message, "blocked");
          submitButton.disabled = false;
          setInputLoading(container, false);
          return;
        }

        // The fast path: this response lands in ~1-3s (moderation +
        // orientation only), not 10s-1min — actual previews arrive via
        // polling below, appended one at a time as they're ready.
        state = {
          phrase,
          orientation: data.orientation,
          sizes: data.sizes,
          previews: [],
          sessionId: data.sessionId,
          sessionIds: [data.sessionId],
        };
        recordRecentSearch({ sessionId: data.sessionId, phrase, orientation: data.orientation, sizes: data.sizes });
        const knownIds = new Set();
        showSkeletons(container, EXPECTED_PREVIEW_COUNT);

        pollSearchStatus({
          sessionId: data.sessionId,
          backendUrl,
          knownIds,
          onUpdate: (newPreviews) => {
            replaceSkeletonsWithResults(container, newPreviews, data.sizes, backendUrl, data.orientation);
            state.previews = state.previews.concat(newPreviews);
            showProgress(state.previews.length, false);
            persistState();
          },
          onDone: () => {
            removeRemainingSkeletons(container);
            showProgress(state.previews.length, true);
            persistState();
            startLoadMore(phrase, data.sessionId);
            submitButton.disabled = false;
            setInputLoading(container, false);
          },
          onError: (message) => {
            removeRemainingSkeletons(container);
            setStatus(container, message || searchStrings.networkError || "Network error — please try again.", "error");
            submitButton.disabled = false;
            setInputLoading(container, false);
          },
        });
      } catch (err) {
        setStatus(container, searchStrings.networkError || "Network error — please try again.", "error");
        submitButton.disabled = false;
        setInputLoading(container, false);
      }
    }

    restoreState();
  }

  document.querySelectorAll("[data-backend-url]").forEach(initAiSearch);

  // Exposed so piecework-cart.js (loaded sitewide) can reopen this exact
  // modal for a preview-type cart line item — same size pills/Add to
  // cart/Buy behavior as clicking the design straight out of search
  // results, not a separate cart-only copy.
  window.PieceworkPreviewModal = { open: openPreviewModal };
})();
